import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { omitReservedManagedAuthPrivateData } from '../../config/r2rManagedAuthPrivateData';
import {
  updatedEntities,
  denormalisedEntities,
  denormalisedResponseEntities,
} from '../../util/data';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import {
  listingCardImageFields,
  listingCardImageVariantParams,
} from '../../util/listingImageVariants';
import { parse } from '../../util/urlHelpers';
import { getReferralParams } from '../../util/webStorageHelpers';
import { refreshXfUserGroups } from '../../util/api';
import {
  buildListingCapContext,
  getClientR2rEnvironment,
  isAtListingCap,
  OPEN_LISTING_STATES,
} from '../../util/listingCaps';
import { canUseListingImport } from '../../util/listingImport/canUseListingImport';
import { IMPORT_PURCHASE_LISTING_TYPES } from '../../util/listingImport/constants';
import { hasFinishTableListings } from '../../util/listingImport/finishTableRows';
import {
  getEffectiveStoredUserType,
  stripMarketplaceFeeParenthetical,
} from '../../util/userHelpers';
import { LISTING_STATE_DRAFT } from '../../util/types';

import { fetchCurrentUser, setCurrentUser } from '../../ducks/user.duck';

import {
  isHideClosedActiveFromStates,
  isManageListingsFilterOn,
  getManageListingsFiltersFromUser,
  MANAGE_LISTINGS_FILTERS_PROFILE_KEY,
  ownListingsQueryMatches,
  removeClosedListingFromManageListingsPage,
  resolveManageListingsFilters,
  RESULT_PAGE_SIZE,
  statesForManageListingsFilters,
} from './ManageListingsPage.filters';

const MANAGE_LISTINGS_FILTER_KEYS = ['hideClosed', 'hideDrafts'];
const PROFILE_LISTINGS_URL_KEYS = ['tab', 'category', 'subcategory', 'sort'];

// ================ Selectors ================ //

/**
 * Get the denormalised own listing entities with the given IDs
 *
 * @param {Object} state the full Redux store
 * @param {Array<UUID>} listingIds listing IDs to select from the store
 */
export const getOwnListingsById = (state, listingIds) => {
  const { ownEntities } = state.ManageListingsPage;
  const resources = listingIds.map(id => ({
    id,
    type: 'ownListing',
  }));
  const throwIfNotFound = false;
  return denormalisedEntities(ownEntities, resources, throwIfNotFound).filter(
    listing => listing && !listing.attributes?.deleted
  );
};

// ================ Async Thunks ================ //

////////////////////////
// Query Own Listings //
////////////////////////
const queryOwnListingsPayloadCreator = (queryParams, { extra: sdk, dispatch, rejectWithValue }) => {
  const { perPage, ...rest } = queryParams;
  const params = { ...rest, perPage };

  return sdk.ownListings
    .query(params)
    .then(response => {
      dispatch(addOwnEntities(response));
      return response;
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const queryOwnListingsThunk = createAsyncThunk(
  'app/ManageListingsPage/queryOwnListings',
  queryOwnListingsPayloadCreator
);
// Backward compatible wrapper for the thunk
export const queryOwnListings = queryParams => (dispatch, getState, sdk) => {
  return dispatch(queryOwnListingsThunk(queryParams)).unwrap();
};

export const fetchHasFinishTableDrafts = async sdk => {
  const queryDrafts = params =>
    sdk.ownListings.query({
      states: [LISTING_STATE_DRAFT],
      page: 1,
      ...params,
    });

  let response;
  try {
    response = await queryDrafts({
      perPage: 1,
      pub_listingType: IMPORT_PURCHASE_LISTING_TYPES.join(','),
    });
  } catch {
    // ownListings.query may reject unknown pub_ filters; scan one unfiltered draft page.
    response = await queryDrafts({ perPage: 100 });
  }

  const resources = Array.isArray(response?.data?.data) ? response.data.data : [];
  return hasFinishTableListings(resources);
};

export const queryHasFinishTableDraftsThunk = createAsyncThunk(
  'app/ManageListingsPage/queryHasFinishTableDrafts',
  async (_, { extra: sdk, rejectWithValue }) => {
    try {
      return await fetchHasFinishTableDrafts(sdk);
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

///////////////////////////////
// Query Open Listings Count //
///////////////////////////////

const getUserTypeLabel = (config, userTypeId) => {
  const userType = (config?.user?.userTypes || []).find(type => type.userType === userTypeId);
  const label = userType?.label || userTypeId;
  return stripMarketplaceFeeParenthetical(label) || userTypeId;
};

const getStoredXfUserGroupIds = currentUser =>
  currentUser?.attributes?.profile?.privateData?.xfUserGroupIds || [];

export const loadListingCapContextThunk = createAsyncThunk(
  'app/ManageListingsPage/loadListingCapContext',
  async ({ config }, { getState, rejectWithValue, extra: sdk }) => {
    const currentUser = getState().user.currentUser;
    const storedXfUserGroupIds = getStoredXfUserGroupIds(currentUser);
    const groupRefresh = await refreshXfUserGroups().catch(() => ({
      refreshed: false,
      xfUserGroupIds: storedXfUserGroupIds,
    }));
    const xfUserGroupIds = Array.isArray(groupRefresh?.xfUserGroupIds)
      ? groupRefresh.xfUserGroupIds
      : storedXfUserGroupIds;

    try {
      const response = await sdk.ownListings.query({
        states: OPEN_LISTING_STATES,
        page: 1,
        perPage: 1,
      });
      const openCount = Number(response?.data?.meta?.totalItems ?? 0) || 0;
      const userTypeId = getEffectiveStoredUserType(config, currentUser);

      return buildListingCapContext({
        userTypeId,
        xfUserGroupIds,
        environment: getClientR2rEnvironment(groupRefresh),
        openCount,
        userTypeLabel: getUserTypeLabel(config, userTypeId),
      });
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

export const refreshOpenListingsCountThunk = createAsyncThunk(
  'app/ManageListingsPage/refreshOpenListingsCount',
  async (_, { rejectWithValue, extra: sdk }) => {
    try {
      const response = await sdk.ownListings.query({
        states: OPEN_LISTING_STATES,
        page: 1,
        perPage: 1,
      });
      return Number(response?.data?.meta?.totalItems ?? 0) || 0;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

///////////////////
// Close Listing //
///////////////////
const closeListingPayloadCreator = (listingId, { extra: sdk, getState, rejectWithValue }) => {
  return sdk.ownListings
    .close({ id: listingId }, { expand: true })
    .then(closeResponse => {
      const { queryParams } = getState().ManageListingsPage;
      const hideClosedActive = isHideClosedActiveFromStates(queryParams?.states);
      return { closeResponse, hideClosedActive };
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const closeListingThunk = createAsyncThunk(
  'app/ManageListingsPage/closeListing',
  closeListingPayloadCreator
);
// Backward compatible wrapper for the thunk
export const closeListing = listingId => (dispatch, getState, sdk) => {
  return dispatch(closeListingThunk(listingId))
    .unwrap()
    .then(response => {
      dispatch(refreshOpenListingsCountThunk());
      return response;
    });
};

//////////////////
// Open Listing //
//////////////////
const openListingPayloadCreator = async (listingId, { extra: sdk, getState, rejectWithValue }) => {
  const listingCap = getState().ManageListingsPage.listingCap;
  if (!listingCap || typeof listingCap.max !== 'number') {
    return rejectWithValue({
      code: 'listing-cap-unknown',
      message: 'Open-listing cap could not be verified.',
    });
  }

  try {
    const countRes = await sdk.ownListings.query({
      states: OPEN_LISTING_STATES,
      page: 1,
      perPage: 1,
    });
    const openCount = Number(countRes?.data?.meta?.totalItems ?? 0) || 0;
    if (isAtListingCap(openCount, listingCap.max)) {
      return rejectWithValue({
        code: 'listing-cap-reached',
        message: 'Cannot reopen a listing because the listing cap has been reached.',
        openCount,
        max: listingCap.max,
      });
    }
  } catch (e) {
    return rejectWithValue({
      code: 'listing-cap-check-failed',
      message: 'Open-listing cap check failed.',
    });
  }

  try {
    return await sdk.ownListings.open({ id: listingId }, { expand: true });
  } catch (e) {
    return rejectWithValue(storableError(e));
  }
};

export const openListingThunk = createAsyncThunk(
  'app/ManageListingsPage/openListing',
  openListingPayloadCreator
);
// Backward compatible wrapper for the thunk
export const openListing = listingId => (dispatch, getState, sdk) => {
  return dispatch(openListingThunk(listingId))
    .unwrap()
    .then(async response => {
      // Await recount so a rapid second reopen sees the updated openCount.
      try {
        await dispatch(refreshOpenListingsCountThunk()).unwrap();
      } catch (e) {
        log.error(e, 'manage-listings-open-count-refresh-failed');
      }
      return response;
    });
};

///////////////////
// Discard Draft //
///////////////////

const delay = ms => new Promise(resolve => window.setTimeout(resolve, ms));

const discardDraftPayloadCreator = (listingId, thunkAPI) => {
  const { getState, extra: sdk, dispatch, rejectWithValue } = thunkAPI;
  const { queryParams } = getState().ManageListingsPage;

  return sdk.ownListings
    .discardDraft({ id: listingId }, { expand: true })
    .then(() => {
      // Return the listing update with a delay, so that the user
      // notices which listing gets removed
      return Promise.all([delay(300), sdk.ownListings.query(queryParams)]);
    })
    .then(([_, listingResponse]) => {
      dispatch(addOwnEntities(listingResponse));
      return listingResponse;
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const discardDraftThunk = createAsyncThunk(
  'app/ManageListingsPage/discardDraft',
  discardDraftPayloadCreator
);
// Backward compatible wrapper for the thunk
export const discardDraft = listingId => (dispatch, getState, sdk) => {
  return dispatch(discardDraftThunk(listingId))
    .unwrap()
    .then(response => {
      dispatch(refreshOpenListingsCountThunk());
      if (canUseListingImport(getState().user?.currentUser)) {
        dispatch(queryHasFinishTableDraftsThunk());
      }
      return response;
    });
};

// ================ Slice ================ //

const resultIds = data => data.data.map(l => l.id);

const updateListingAttributes = (state, listingEntity) => {
  const oldListing = state.ownEntities.ownListing[listingEntity.id.uuid];
  const updatedListing = { ...oldListing, attributes: listingEntity.attributes };
  const ownListingEntities = {
    ...state.ownEntities.ownListing,
    [listingEntity.id.uuid]: updatedListing,
  };
  return {
    ...state,
    ownEntities: { ...state.ownEntities, ownListing: ownListingEntities },
  };
};

const manageListingsPageSlice = createSlice({
  name: 'ManageListingsPage',
  initialState: {
    pagination: null,
    queryParams: null,
    queryInProgress: false,
    queryListingsError: null,
    currentPageResultIds: [],
    ownEntities: {},
    openingListing: null,
    openingListingError: null,
    closingListing: null,
    closingListingError: null,
    discardingDraft: null,
    discardingDraftError: null,
    listingCap: null,
    hasFinishTableDrafts: false,
  },
  reducers: {
    clearOpenListingError: state => {
      state.openingListing = null;
      state.openingListingError = null;
    },
    addOwnEntities: (state, action) => {
      // This works the same way as addMarketplaceEntities,
      // but we don't want to mix own listings with searched listings
      // (own listings data contains different info - e.g. exact location etc.)
      const apiResponse = action.payload.data;
      state.ownEntities = updatedEntities({ ...state.ownEntities }, apiResponse);
    },
  },
  extraReducers: builder => {
    // Query own listings
    builder
      .addCase(queryOwnListingsThunk.pending, (state, action) => {
        state.queryParams = action.meta.arg;
        state.queryInProgress = true;
        state.queryListingsError = null;
        state.currentPageResultIds = [];
      })
      .addCase(queryOwnListingsThunk.fulfilled, (state, action) => {
        state.currentPageResultIds = resultIds(action.payload.data);
        state.pagination = action.payload.data.meta;
        state.queryInProgress = false;
      })
      .addCase(queryOwnListingsThunk.rejected, (state, action) => {
        console.error(action.payload || action.error);
        state.queryInProgress = false;
        state.queryListingsError = action.payload;
      });

    // Listing cap context
    builder
      .addCase(loadListingCapContextThunk.fulfilled, (state, action) => {
        state.listingCap = action.payload;
      })
      .addCase(loadListingCapContextThunk.rejected, (state, action) => {
        console.error(action.payload || action.error);
      })
      .addCase(refreshOpenListingsCountThunk.fulfilled, (state, action) => {
        if (state.listingCap) {
          state.listingCap.openCount = action.payload;
          state.listingCap.isAtCap = isAtListingCap(action.payload, state.listingCap.max);
        }
      })
      .addCase(refreshOpenListingsCountThunk.rejected, (state, action) => {
        console.error(action.payload || action.error);
      });

    builder
      .addCase(queryHasFinishTableDraftsThunk.fulfilled, (state, action) => {
        state.hasFinishTableDrafts = !!action.payload;
      })
      .addCase(queryHasFinishTableDraftsThunk.rejected, (state, action) => {
        console.error(action.payload || action.error);
        state.hasFinishTableDrafts = false;
      });

    // Open listing
    builder
      .addCase(openListingThunk.pending, (state, action) => {
        state.openingListing = action.meta.arg;
        state.openingListingError = null;
      })
      .addCase(openListingThunk.fulfilled, (state, action) => {
        const listing = action.payload.data.data;
        const updatedState = updateListingAttributes(state, listing);
        state.ownEntities = updatedState.ownEntities;
        state.openingListing = null;
      })
      .addCase(openListingThunk.rejected, (state, action) => {
        console.error(action.payload || action.error);
        if (
          action.payload?.code === 'listing-cap-reached' &&
          state.listingCap &&
          typeof action.payload.openCount === 'number'
        ) {
          state.listingCap.openCount = action.payload.openCount;
          state.listingCap.isAtCap = isAtListingCap(action.payload.openCount, state.listingCap.max);
        }
        state.openingListingError = {
          listingId: state.openingListing,
          error: action.payload,
        };
        state.openingListing = null;
      });

    // Close listing
    builder
      .addCase(closeListingThunk.pending, (state, action) => {
        state.closingListing = action.meta.arg;
        state.closingListingError = null;
      })
      .addCase(closeListingThunk.fulfilled, (state, action) => {
        const { closeResponse, hideClosedActive } = action.payload;
        const listing = closeResponse.data.data;
        const updatedState = updateListingAttributes(state, listing);
        state.ownEntities = updatedState.ownEntities;
        state.closingListing = null;
        if (hideClosedActive) {
          const next = removeClosedListingFromManageListingsPage({
            currentPageResultIds: state.currentPageResultIds,
            pagination: state.pagination,
            closedListingId: listing.id,
          });
          state.currentPageResultIds = next.currentPageResultIds;
          state.pagination = next.pagination;
        }
      })
      .addCase(closeListingThunk.rejected, (state, action) => {
        console.error(action.payload || action.error);
        state.closingListingError = {
          listingId: state.closingListing,
          error: action.payload,
        };
        state.closingListing = null;
      });

    // Discard draft
    builder
      .addCase(discardDraftThunk.pending, (state, action) => {
        state.discardingDraft = action.meta.arg;
        state.discardingDraftError = null;
      })
      .addCase(discardDraftThunk.fulfilled, (state, action) => {
        state.currentPageResultIds = resultIds(action.payload.data);
        state.pagination = action.payload.meta;
        state.discardingDraft = null;
      })
      .addCase(discardDraftThunk.rejected, (state, action) => {
        console.error(action.payload || action.error);
        state.discardingDraftError = {
          listingId: state.discardingDraft,
          error: action.payload,
        };
        state.discardingDraft = null;
      });
  },
});

export const { clearOpenListingError, addOwnEntities } = manageListingsPageSlice.actions;
export default manageListingsPageSlice.reducer;

///////////////////////////////
// Persist hide-filter prefs //
///////////////////////////////

/**
 * Save Manage Listings hide filters to currentUser.privateData.
 * Optimistic update; failures are logged and not reverted.
 *
 * @param {{ hideClosed: boolean, hideDrafts: boolean }} filters
 */
export const saveManageListingsFiltersThunk = createAsyncThunk(
  'app/ManageListingsPage/saveManageListingsFilters',
  (filters, { getState, extra: sdk, dispatch, rejectWithValue }) => {
    const currentUser = getState().user.currentUser;
    if (!currentUser) {
      return rejectWithValue(storableError(new Error('Not authenticated')));
    }

    const manageListingsFilters = {
      hideClosed: isManageListingsFilterOn(filters?.hideClosed),
      hideDrafts: isManageListingsFilterOn(filters?.hideDrafts),
    };

    const optimisticUser = {
      ...currentUser,
      attributes: {
        ...currentUser.attributes,
        profile: {
          ...currentUser.attributes.profile,
          privateData: {
            ...currentUser.attributes.profile?.privateData,
            [MANAGE_LISTINGS_FILTERS_PROFILE_KEY]: manageListingsFilters,
          },
        },
      },
    };
    dispatch(setCurrentUser(optimisticUser));

    const privateData = omitReservedManagedAuthPrivateData({
      [MANAGE_LISTINGS_FILTERS_PROFILE_KEY]: manageListingsFilters,
    });

    return sdk.currentUser
      .updateProfile({ privateData }, { expand: true })
      .then(response => {
        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        // Ignore stale responses if the user toggled again while this request was in flight.
        const latest = getManageListingsFiltersFromUser(getState().user.currentUser);
        if (
          latest.hideClosed !== manageListingsFilters.hideClosed ||
          latest.hideDrafts !== manageListingsFilters.hideDrafts
        ) {
          return manageListingsFilters;
        }
        // Keep the in-memory user. The update response can omit privateData keys
        // that are not part of this write.
        const previous = getState().user.currentUser;
        const savedUser = {
          ...previous,
          attributes: {
            ...previous?.attributes,
            profile: {
              ...previous?.attributes?.profile,
              privateData: {
                ...previous?.attributes?.profile?.privateData,
                ...entities[0].attributes?.profile?.privateData,
                [MANAGE_LISTINGS_FILTERS_PROFILE_KEY]: manageListingsFilters,
              },
            },
          },
        };
        dispatch(setCurrentUser(savedUser));
        return manageListingsFilters;
      })
      .catch(e => {
        log.error(e, 'manage-listings-filters-update-failed');
        return rejectWithValue(storableError(e));
      });
  }
);

export const saveManageListingsFilters = filters => dispatch => {
  return dispatch(saveManageListingsFiltersThunk(filters));
};

// ================ Load data ================ //

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const queryParams = parse(search);
  const page = queryParams.page || 1;
  dispatch(clearOpenListingError());

  // Filter out referral + UI filter params so they are not included in the API query
  const { userTypes = [] } = config.user || {};
  const validReferralSources = getReferralParams(userTypes);
  const apiParamsRaw = Object.fromEntries(
    Object.entries(queryParams).filter(([key]) => {
      return (
        !validReferralSources.includes(key) &&
        !MANAGE_LISTINGS_FILTER_KEYS.includes(key) &&
        !PROFILE_LISTINGS_URL_KEYS.includes(key)
      );
    })
  );

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  // Await currentUser first so saved hide filters can shape the listings query.
  // Profile-tab reloads skip this fetch: a currentUser.show() started before the
  // hide-filter save lands overwrites the optimistic privateData and the toggle snaps back.
  const currentUserPromise = params?.skipCurrentUserFetch
    ? Promise.resolve(getState().user.currentUser)
    : dispatch(fetchCurrentUser());

  return currentUserPromise
    .then(currentUser => {
      const { hideClosed, hideDrafts } = resolveManageListingsFilters({
        queryParams,
        currentUser,
      });
      // Profile tab filters the loaded page in the UI, so the query keeps every state.
      const states = params?.omitStateFilters
        ? null
        : statesForManageListingsFilters({ hideClosed, hideDrafts });
      if (canUseListingImport(currentUser)) {
        dispatch(queryHasFinishTableDraftsThunk());
      }

      // URL sync (history.replace) re-runs loadData; skip refetch when page+states match.
      const {
        queryParams: prevQueryParams,
        queryInProgress,
        queryListingsError,
      } = getState().ManageListingsPage;
      if (
        !queryInProgress &&
        !queryListingsError &&
        ownListingsQueryMatches(prevQueryParams, { page, states })
      ) {
        return null;
      }

      return dispatch(
        queryOwnListings({
          ...apiParamsRaw,
          page,
          perPage: RESULT_PAGE_SIZE,
          ...(states ? { states } : {}),
          include: ['images', 'currentStock'],
          'fields.image': listingCardImageFields(variantPrefix),
          ...listingCardImageVariantParams(variantPrefix, aspectRatio),
          'limit.images': 1,
        })
      );
    })
    .then(async response => {
      const capContextAction = await dispatch(loadListingCapContextThunk({ config }));
      if (loadListingCapContextThunk.rejected.match(capContextAction)) {
        log.error(
          capContextAction.payload || capContextAction.error,
          'manage-listings-cap-context-failed'
        );
      }
      return response?.data?.data;
    })
    .catch(e => {
      throw e;
    });
};
